import React, { useState, useEffect, useContext } from 'react';
import { motion } from 'framer-motion';
import { DevModeContext } from '../App';

function InstallPWA() {
  const [supportsPWA, setSupportsPWA] = useState(false);
  const [promptEvent, setPromptEvent] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const isDevMode = useContext(DevModeContext);

  useEffect(() => {
    // Check if user is on mobile
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      return /android|iphone|ipad|ipod|webos/i.test(userAgent);
    };
    
    setIsMobile(checkMobile());

    const handler = (e) => {
      e.preventDefault();
      console.log('Before install prompt captured');
      setSupportsPWA(true);
      setPromptEvent(e);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true) {
      setIsInstalled(true);
    }

    // In development mode, simulate the install prompt
    if (isDevMode && isMobile) {
      console.log('Dev mode: Simulating PWA install prompt');
      setSupportsPWA(true);
      setShowPrompt(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [isDevMode, isMobile]);

  const onInstallClick = () => {
    if (!promptEvent) {
      if (isDevMode) {
        console.log('Dev mode: This would trigger the actual install prompt in production');
        alert('In production, this would install the app. Development mode detected.');
      }
      return;
    }
    
    promptEvent.prompt();
    
    promptEvent.userChoice.then((choiceResult) => {
      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the install prompt');
        setIsInstalled(true);
      } else {
        console.log('User dismissed the install prompt');
      }
      setShowPrompt(false);
    });
  };

  // Don't show on desktop unless in dev mode
  if ((!supportsPWA || isInstalled || !showPrompt || !isMobile) && !(isDevMode && isMobile)) {
    return null;
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 50 }}
      className="fixed bottom-4 right-4 z-50"
    >
      <div className="backdrop-blur-lg dark:bg-white/10 bg-white/80 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl p-4 max-w-sm">
        {isDevMode && (
          <div className="mb-2 bg-yellow-500/90 text-black text-xs rounded-lg p-1.5 font-semibold backdrop-blur-sm flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            DEV MODE: PWA Testing
          </div>
        )}
        <div className="flex items-start mb-3">
          <div className="flex-grow">
            <div className="flex items-center mb-1">
              <div className="w-8 h-8 mr-2 flex items-center justify-center rounded-full bg-gradient-to-r from-purple-500 to-indigo-600 text-white">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </div>
              <h3 className="text-gray-900 dark:text-white font-semibold text-lg">Install Cashsule App</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">Add to your home screen for faster access and a better experience!</p>
          </div>
          <button 
            onClick={() => setShowPrompt(false)} 
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
        <div className="flex space-x-3 justify-end">
          <button 
            onClick={() => setShowPrompt(false)} 
            className="text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 py-1.5 px-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm"
          >
            Later
          </button>
          <button 
            onClick={onInstallClick} 
            className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white py-1.5 px-4 rounded-lg hover:shadow-lg hover:from-purple-600 hover:to-indigo-700 transition-all duration-200 text-sm font-medium flex items-center"
          >
            <span>Install Now</span>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default InstallPWA; 